import {
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  UseFormReturn,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';

import { ChevronDown, ChevronUp, Info, MapPin } from 'lucide-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from 'components/Tooltip';
import DateTimeInput from 'components/input/DateTimeInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { StopTypes } from 'types/Appointment';
import { Stop } from 'types/QuoteRequest';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { QuoteChanges } from 'types/suggestions/QuoteSuggestions';
import { asArrayOrObjectValues } from 'utils/asArrayOrObjectValues';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import { QuickQuoteTextInput } from '../../common/QuickQuoteTextInput';
import { LTLAccessorial, QuickQuoteInputs } from '../../types';
import {
  getParsedStopAddressBulletLines,
  parseStopAddressFromLocation,
} from '../../utils/parseStopAddressFromLocation';
import { StopsSection as SharedLTLStopsSection } from '../StopsSection';
import { useTaiCustomerLocations } from '../hooks/useTaiCustomerLocations';
import { ShiplifyLookupSection } from './ShiplifyLookupSection';
import { TaiAddressBookSelect } from './TaiAddressBookSelect';
import { syncParsedStopAddressToForm } from './syncParsedStopAddressToForm';

interface StopsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  hasAddedQuoteDates: boolean;
  integrationName?: Undef<TMS>;
  accessorials?: LTLAccessorial[];
  // AI-filled hints; defaults to the clicked Kit suggestion. Batch LTL passes each
  // shipment's own suggestion (null for a shipment the rep added).
  suggestedFields?: Maybe<QuoteChanges>;
}

const locationInputProps = {
  placeholder: 'City, State or ZIP or Full address',
  inputClassName: 'placeholder:text-[12px]',
  required: true,
} as const;

const taiStopLocationTooltipContentClassName = cn(
  'break-words bg-neutral-50 border-neutral-200 px-3 py-2',
  'text-left text-xs leading-relaxed text-neutral-700'
);

const taiStopLocationFormatTooltipClassName = cn(
  'max-w-[min(22rem,calc(100vw-2rem))] px-4 py-3 pb-4 text-left text-xs leading-relaxed',
  'text-neutral-700 shadow-lg bg-neutral-50 border-neutral-200'
);

const TAI_STOP_LOCATION_PARSE_HELPER_MESSAGE =
  "We couldn't identify a valid ZIP/postal code or City + State from this location.";
const TAI_WALL_CLOCK_TIMEZONE = 'UTC';

type StopLocationFieldName = 'stops.0.location' | 'stops.1.location';

type StopLocationFormatOption = {
  key: string;
  label: string;
};

const TAI_STOP_LOCATION_FORMAT_OPTIONS: StopLocationFormatOption[] = [
  {
    key: 'zip',
    label: 'ZIP',
  },
  {
    key: 'city-state',
    label: 'City, State',
  },
  {
    key: 'city-state-zip',
    label: 'City, State ZIP',
  },
  {
    key: 'full-address',
    label: 'Full Address',
  },
];

function TaiStopLocationFormatTooltipContent() {
  return (
    <>
      <p className='mb-3 text-left text-xs text-neutral-500'>
        Any of the following for US and Canadian locations can be entered
      </p>
      <ul className='m-0 list-none space-y-2.5 p-0 pl-0 text-left'>
        {TAI_STOP_LOCATION_FORMAT_OPTIONS.map((item) => (
          <li key={item.key} className='flex gap-2 text-left'>
            <span className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400' />
            <span className='text-neutral-500'>{item.label}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

function StopLocationFormatInfoIcon({ label }: { label: string }) {
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button
          type='button'
          className='inline-flex items-center'
          aria-label={`${label} location format help`}
        >
          <Info className='h-4 w-4 text-neutral-500' />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side='bottom'
        sideOffset={8}
        className={taiStopLocationFormatTooltipClassName}
      >
        <TaiStopLocationFormatTooltipContent />
      </TooltipContent>
    </Tooltip>
  );
}

function ParsedStopAddressTooltipContent({ location }: { location: string }) {
  const parsed = parseStopAddressFromLocation(location);
  const lines = getParsedStopAddressBulletLines(parsed);

  if (lines.length === 0) {
    return (
      <p className='m-0 text-left text-xs leading-relaxed text-neutral-700'>
        {TAI_STOP_LOCATION_PARSE_HELPER_MESSAGE}
      </p>
    );
  }

  return (
    <ul className='m-0 list-none space-y-1 p-0 text-left text-xs leading-relaxed'>
      {lines.map((line) => (
        <li key={line.key} className='flex gap-1.5 text-left'>
          <span className='shrink-0 text-neutral-500'>{line.label}:</span>
          <span className='text-neutral-800'>{line.value}</span>
        </li>
      ))}
    </ul>
  );
}

function ParsedStopAddressIcon({
  label,
  location,
  tooltipWidthPx,
}: {
  label: string;
  location: string;
  tooltipWidthPx?: number;
}) {
  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <button
          type='button'
          aria-label={`View parsed ${label.toLowerCase()} address`}
          onPointerDown={(event) => event.preventDefault()}
          className='inline-flex h-7 w-7 items-center justify-center rounded-sm text-neutral-500 hover:bg-neutral-100 hover:text-brand-main'
        >
          <MapPin className='h-4 w-4' />
        </button>
      </TooltipTrigger>
      <TooltipPortal container={getDrumkitContainer()}>
        <TooltipContent
          side='bottom'
          sideOffset={6}
          className={cn(
            taiStopLocationTooltipContentClassName,
            'max-w-[min(22rem,calc(100vw-2rem))]'
          )}
          style={
            tooltipWidthPx !== undefined
              ? { width: tooltipWidthPx, maxWidth: tooltipWidthPx }
              : undefined
          }
        >
          <ParsedStopAddressTooltipContent location={location} />
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
}

function TaiStopLocationField({
  name,
  label,
}: {
  name: StopLocationFieldName;
  label: string;
}) {
  const { control, setValue, getValues, getFieldState, formState } =
    useFormContext<QuickQuoteInputs>();
  const stopIndex = name === 'stops.0.location' ? 0 : 1;
  const stopLabel = stopIndex === 0 ? 'Pickup' : 'Dropoff';
  const fieldRef = useRef<HTMLDivElement>(null);
  const [tooltipWidthPx, setTooltipWidthPx] = useState<Undef<number>>();
  const locationValue =
    useWatch({ control, name })?.trim() ?? getValues(name)?.trim() ?? '';
  const isLocationInvalid =
    locationValue.length === 0 && Boolean(getFieldState(name, formState).error);

  const updateTooltipWidth = useCallback(() => {
    const width = fieldRef.current?.offsetWidth;
    if (width && width > 0) {
      setTooltipWidthPx(width);
    }
  }, []);

  useLayoutEffect(() => {
    updateTooltipWidth();
  }, [locationValue, updateTooltipWidth]);

  useLayoutEffect(() => {
    const element = fieldRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(updateTooltipWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [updateTooltipWidth]);

  const syncParsedFieldsFromLocation = useCallback(() => {
    const location = (getValues(name) ?? '').trim();
    if (!location) {
      return;
    }

    const parsed = parseStopAddressFromLocation(location);
    if (!parsed) {
      return;
    }

    syncParsedStopAddressToForm(stopIndex, parsed, getValues, setValue);
  }, [getValues, name, setValue, stopIndex]);

  const locationBlurOptions = {
    onBlur: syncParsedFieldsFromLocation,
  };

  return (
    <div ref={fieldRef} className='w-full min-w-0'>
      <QuickQuoteTextInput
        name={name}
        label={label}
        labelAdornment={<StopLocationFormatInfoIcon label={stopLabel} />}
        {...locationInputProps}
        options={locationBlurOptions}
        inputClassName={cn(
          locationInputProps.inputClassName,
          'truncate',
          isLocationInvalid && '!border-error-500 !bg-error-50'
        )}
        inputEndAdornment={
          locationValue ? (
            <ParsedStopAddressIcon
              label={stopLabel}
              location={locationValue}
              tooltipWidthPx={tooltipWidthPx}
            />
          ) : undefined
        }
      />
    </div>
  );
}

function hasAiSuggestedDate(value: unknown): boolean {
  if (!value) {
    return false;
  }

  const date = value instanceof Date ? value : new Date(value as string);
  return !Number.isNaN(date.getTime());
}

function TaiStopDateTimeCollapsible({
  title,
  defaultOpen,
  forceOpen = false,
  children,
}: {
  title: string;
  defaultOpen: boolean;
  forceOpen?: boolean;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useLayoutEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
    }
  }, [forceOpen]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className='w-full mx-0'>
      <CollapsibleTrigger type='button' className='w-full cursor-pointer py-1'>
        <Flex align='center' justify='between' className='w-full'>
          <Typography
            variant='body-sm'
            weight='medium'
            className='text-neutral-700 whitespace-nowrap'
          >
            {title}
          </Typography>
          {isOpen ? (
            <ChevronUp className='h-4 w-4 text-neutral-500' />
          ) : (
            <ChevronDown className='h-4 w-4 text-neutral-500' />
          )}
        </Flex>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Flex direction='col' gap='xs' className='w-full mx-0 pt-1'>
          {children}
        </Flex>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Tai LTL stops. Full street parse + tooltip only for TMS.Tai; other Tai-form
 * integrations (e.g. McLeod) keep the shared LTL stops section behavior.
 */
export function StopsSection({
  formMethods,
  hasAddedQuoteDates,
  integrationName,
  accessorials = [],
  suggestedFields: suggestedFieldsOverride,
}: StopsSectionProps) {
  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);
  const { control } = formMethods;
  // Must run before any early return (rules-of-hooks). Unused when not Tai.
  const { errors } = useFormState({ control });
  const {
    locations,
    setLocations,
    isLoadingLocations,
    tmsID,
    customerID,
    customerExternalTmsId,
    hasCustomer,
  } = useTaiCustomerLocations();

  if (integrationName !== TMS.Tai) {
    return (
      <SharedLTLStopsSection
        formMethods={formMethods}
        hasAddedQuoteDates={hasAddedQuoteDates}
      />
    );
  }

  const suggestedFields =
    suggestedFieldsOverride === undefined
      ? (clickedSuggestion?.suggested as QuoteChanges | undefined)
      : suggestedFieldsOverride;
  // Ready/close defaults use now()/ready+4h — those must not show AI-filled.
  // Only show the hint when the suggestion actually provided that date.
  const hidePickupReadyAIHint = !hasAiSuggestedDate(
    suggestedFields?.pickupDate
  );
  const hideDeliveryEstimatedAIHint = !hasAiSuggestedDate(
    suggestedFields?.deliveryDate
  );
  const pickupDateTimeHasError = Boolean(
    errors.pickupDate || errors.pickupCloseDate
  );
  const dropoffDateTimeHasError = Boolean(
    errors.deliveryDate || errors.deliveryCloseDate
  );

  const suggestedStops = asArrayOrObjectValues(
    suggestedFields?.stops as Undef<Stop[] | Record<string, Stop>>
  );
  const suggestedPickupExternalTMSID =
    suggestedStops.find((stop) => stop.stopType === 'pickup')?.externalTMSID ??
    suggestedStops[0]?.externalTMSID;
  const suggestedDropoffExternalTMSID =
    [...suggestedStops].reverse().find((stop) => stop.stopType === 'dropoff')
      ?.externalTMSID ??
    suggestedStops[suggestedStops.length - 1]?.externalTMSID;

  const addressBookSelectProps = {
    locations,
    setLocations,
    isLoadingLocations,
    tmsID,
    customerID,
    customerExternalTmsId,
    hasCustomer,
    accessorials,
  } as const;

  return (
    <Flex direction='col' gap='sm' className='w-full mx-0'>
      <Grid cols='1' gap='xs' className='w-full mx-0'>
        <Typography variant='body' weight='semibold' className='mb-1'>
          Pickup
        </Typography>
        <TaiAddressBookSelect
          stopIndex={0}
          {...addressBookSelectProps}
          aiHighlightedExternalTMSID={suggestedPickupExternalTMSID}
        />
        <TaiStopLocationField name='stops.0.location' label='Location' />
        <ShiplifyLookupSection
          formMethods={formMethods}
          stopIndex={0}
          stopType={StopTypes.Pickup}
        />
        <TaiStopDateTimeCollapsible
          title='Date and Time'
          defaultOpen
          forceOpen={pickupDateTimeHasError}
        >
          <DateTimeInput
            control={control}
            name='pickupDate'
            label='Ready'
            required
            hideAIHint={hidePickupReadyAIHint}
            containerClassName='w-full gap-0'
            timezone={TAI_WALL_CLOCK_TIMEZONE}
            options={{ required: 'Ready is required' }}
          />
          <DateTimeInput
            control={control}
            name='pickupCloseDate'
            label='Close'
            required
            hideAIHint
            containerClassName='w-full gap-0'
            timezone={TAI_WALL_CLOCK_TIMEZONE}
            options={{ required: 'Close is required' }}
          />
        </TaiStopDateTimeCollapsible>
      </Grid>

      <Grid cols='1' gap='xs' className='w-full mx-0'>
        <Typography variant='body' weight='semibold' className='my-1'>
          Dropoff
        </Typography>
        <TaiAddressBookSelect
          stopIndex={1}
          {...addressBookSelectProps}
          aiHighlightedExternalTMSID={suggestedDropoffExternalTMSID}
        />
        <TaiStopLocationField name='stops.1.location' label='Location' />
        <ShiplifyLookupSection
          formMethods={formMethods}
          stopIndex={1}
          stopType={StopTypes.Dropoff}
        />
        <TaiStopDateTimeCollapsible
          title='Date and Time (optional)'
          defaultOpen={false}
          forceOpen={dropoffDateTimeHasError}
        >
          <DateTimeInput
            control={control}
            name='deliveryDate'
            label='Estimated'
            hideAIHint={hideDeliveryEstimatedAIHint}
            containerClassName='w-full gap-0'
            timezone={TAI_WALL_CLOCK_TIMEZONE}
          />
          <DateTimeInput
            control={control}
            name='deliveryCloseDate'
            label='Close'
            hideAIHint
            containerClassName='w-full gap-0'
            timezone={TAI_WALL_CLOCK_TIMEZONE}
          />
        </TaiStopDateTimeCollapsible>
      </Grid>
    </Flex>
  );
}
